import { useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import BkPage from '../../Components/ui/BkPage';
import BkPanel from '../../Components/ui/BkPanel';
import { useAuthContext } from '../../contexts/AuthContext';
import { canUseEmote } from '../../emotes/registry';
import usePageMeta from '../../hooks/use-page-meta';
import { PLAYER_SKINS } from '../../player-colors';
import { canUseAvatar } from '../../profile-avatars/registry';
import StoreItemModal from './StoreItemModal';
import StoreItemTile, { type StoreItem } from './StoreItemTile';
import {
  STORE_CATEGORIES,
  type OwnershipFilter,
  type StoreCategory,
  filterStoreItems,
  groupByRarity,
  rarityRank,
  useStoreCatalog,
} from './store-catalog';

const RARITY_FILTERS = [
  'all',
  'legendary',
  'rare',
  'uncommon',
  'common',
] as const;

function isStoreCategory(value: string | undefined): value is StoreCategory {
  return STORE_CATEGORIES.some((c) => c.id === value);
}

export default function StoreCategoryPage() {
  const { category: rawCategory } = useParams();
  const category = isStoreCategory(rawCategory) ? rawCategory : undefined;
  const meta = STORE_CATEGORIES.find((c) => c.id === category);
  const [searchParams, setSearchParams] = useSearchParams();
  const { byCategory, loading } = useStoreCatalog();
  const [selected, setSelected] = useState<StoreItem | undefined>();

  const query = searchParams.get('q') ?? '';
  const rarity = searchParams.get('rarity') ?? 'all';
  const ownership = (searchParams.get('owned') as OwnershipFilter) || 'all';
  const legacySeasonId = searchParams.get('legacy') ?? 'all';

  usePageMeta({
    title: meta ? `${meta.label} — Store` : 'Store',
    description: meta?.blurb ?? 'Blind Kings store',
    path: meta?.path ?? '/store',
  });

  const {
    userData: {
      ownedColorIds,
      ownedIconIds,
      ownedEmoteIds,
      equippedColorId,
      altSkinId,
      avatarId,
      featuredEmoteId,
    } = {},
  } = useAuthContext();

  const ownedSkins = useMemo(
    () => [
      ...new Set([
        ...PLAYER_SKINS.filter((c) => c.isDefault).map((c) => c.id),
        ...(Array.isArray(ownedColorIds) ? ownedColorIds.map(String) : []),
      ]),
    ],
    [ownedColorIds]
  );

  function isOwned(item: StoreItem) {
    if (item.category === 'gems') return false;
    if (item.category === 'boards') return false;
    if (item.category === 'icons') {
      return canUseAvatar(String(item.iconId ?? ''), ownedColorIds, ownedIconIds);
    }
    if (item.category === 'emotes') {
      return canUseEmote(
        String(item.emoteId ?? ''),
        ownedColorIds,
        ownedEmoteIds
      );
    }
    if (item.category === 'skins' || item.category === 'colors') {
      const id = String(item.skinId ?? item.colorId ?? item.itemId);
      return ownedSkins.includes(id);
    }
    return false;
  }

  function badge(item: StoreItem) {
    if (item.category === 'icons') {
      const id = String(item.iconId ?? '');
      if (avatarId === id) return 'Equipped';
      if (isOwned(item)) return 'Owned';
      return null;
    }
    if (item.category === 'emotes') {
      const id = String(item.emoteId ?? '');
      if (featuredEmoteId === id) return 'Featured';
      if (isOwned(item)) return 'Owned';
      return null;
    }
    if (item.category === 'skins' || item.category === 'colors') {
      const id = String(item.skinId ?? item.colorId ?? item.itemId);
      if (equippedColorId === id) return 'Equipped';
      if (altSkinId === id) return 'Alt';
      if (ownedSkins.includes(id)) return 'Owned';
      return null;
    }
    return null;
  }

  const sourceItems = category ? byCategory[category] : [];
  const legacyOptions = useMemo(() => {
    const ids = new Set(
      sourceItems
        .map((i) => i.legacySeasonId)
        .filter((id): id is string => Boolean(id))
    );
    return [...ids];
  }, [sourceItems]);

  const filtered = useMemo(
    () =>
      filterStoreItems(sourceItems, {
        query,
        rarity,
        ownership,
        legacySeasonId,
        isOwned,
      }).sort(
        (a, b) =>
          rarityRank(String(a.rarity)) - rarityRank(String(b.rarity)) ||
          String(a.title).localeCompare(String(b.title))
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- isOwned closes over user data
    [
      sourceItems,
      query,
      rarity,
      ownership,
      legacySeasonId,
      ownedColorIds,
      ownedIconIds,
      ownedEmoteIds,
      ownedSkins,
    ]
  );

  const groups = useMemo(() => groupByRarity(filtered), [filtered]);

  function setParam(key: string, value: string) {
    const next = new URLSearchParams(searchParams);
    if (!value || value === 'all' || (key === 'q' && !value)) {
      next.delete(key);
    } else {
      next.set(key, value);
    }
    setSearchParams(next, { replace: true });
  }

  if (!category || !meta) {
    return (
      <BkPage narrow>
        <BkPanel className="space-y-3 text-center">
          <p className="text-bk-muted">Unknown store category.</p>
          <Link to="/store" className="text-bk-gold hover:text-bk-gold-bright">
            Back to Store
          </Link>
        </BkPanel>
      </BkPage>
    );
  }

  const showOwnership = category !== 'gems' && category !== 'boards';
  const showLegacy =
    (category === 'icons' || category === 'emotes') &&
    (legacyOptions.length > 0 || legacySeasonId !== 'all');

  return (
    <BkPage>
      <div className="mb-4 space-y-3">
        <Link
          to="/store"
          className="text-xs font-semibold uppercase tracking-wider text-bk-muted hover:text-bk-gold"
        >
          ← Store
        </Link>
        <div>
          <h1 className="text-2xl font-bold uppercase tracking-wider text-bk-gold md:text-3xl">
            {meta.label}
          </h1>
          <p className="mt-1 max-w-2xl text-sm text-bk-muted">{meta.blurb}</p>
        </div>
      </div>

      <div className="sticky top-0 z-30 -mx-4 mb-6 space-y-3 border-b border-bk-gold-dim/30 bg-bk-bg/95 px-4 py-3 backdrop-blur-md sm:-mx-6 sm:px-6 lg:-mx-10 lg:px-10">
        <div className="flex flex-wrap gap-2">
          {STORE_CATEGORIES.map((cat) => (
            <Link
              key={cat.id}
              to={cat.path}
              className={[
                'px-3 py-1.5 text-xs font-bold uppercase tracking-wider',
                cat.id === category
                  ? 'border-b-2 border-bk-gold text-bk-gold-bright'
                  : 'border-b-2 border-transparent text-bk-muted hover:text-bk-gold',
              ].join(' ')}
            >
              {cat.label}
            </Link>
          ))}
        </div>

        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <input
            type="search"
            value={query}
            onChange={(e) => setParam('q', e.target.value)}
            placeholder={`Search ${meta.label.toLowerCase()}…`}
            className="w-full border border-bk-border bg-bk-panel px-3 py-2 text-sm text-bk-text placeholder:text-bk-muted focus:border-bk-gold focus:outline-none sm:max-w-xs"
            aria-label="Search"
          />
          <div className="flex flex-wrap gap-1">
            {RARITY_FILTERS.map((r) => (
              <button
                key={r}
                type="button"
                onClick={() => setParam('rarity', r)}
                className={[
                  'px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide',
                  rarity === r
                    ? 'bg-bk-gold/20 text-bk-gold-bright'
                    : 'text-bk-muted hover:text-bk-gold',
                ].join(' ')}
              >
                {r === 'all' ? 'All' : r}
              </button>
            ))}
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          {showOwnership
            ? (
                [
                  ['all', 'All'],
                  ['not_owned', 'Not owned'],
                  ['owned', 'Owned'],
                ] as const
              ).map(([value, label]) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => setParam('owned', value === 'all' ? '' : value)}
                  className={[
                    'border px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide',
                    ownership === value
                      ? 'border-bk-gold text-bk-gold'
                      : 'border-bk-border text-bk-muted hover:border-bk-gold/60',
                  ].join(' ')}
                >
                  {label}
                </button>
              ))
            : null}
          {showLegacy ? (
            <>
              <button
                type="button"
                onClick={() => setParam('legacy', '')}
                className={[
                  'border px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide',
                  legacySeasonId === 'all'
                    ? 'border-bk-gold text-bk-gold'
                    : 'border-bk-border text-bk-muted',
                ].join(' ')}
              >
                All sources
              </button>
              <button
                type="button"
                onClick={() => setParam('legacy', 'none')}
                className={[
                  'border px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide',
                  legacySeasonId === 'none'
                    ? 'border-bk-gold text-bk-gold'
                    : 'border-bk-border text-bk-muted',
                ].join(' ')}
              >
                Current only
              </button>
              {legacyOptions.map((id) => (
                <button
                  key={id}
                  type="button"
                  onClick={() => setParam('legacy', id)}
                  className={[
                    'border px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide',
                    legacySeasonId === id
                      ? 'border-bk-gold text-bk-gold'
                      : 'border-bk-border text-bk-muted',
                  ].join(' ')}
                >
                  Legacy {id.replace(/^season-/, 'S')}
                </button>
              ))}
            </>
          ) : null}
        </div>
      </div>

      {loading ? (
        <p className="text-sm text-bk-muted animate-bk-pulse-soft">Loading…</p>
      ) : !filtered.length ? (
        <BkPanel className="text-sm text-bk-muted">
          No items match these filters.
        </BkPanel>
      ) : category === 'gems' || category === 'boards' ? (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-4">
          {filtered.map((item) => (
            <StoreItemTile
              key={String(item.itemId)}
              item={item}
              badge={badge(item)}
              onClick={setSelected}
            />
          ))}
        </div>
      ) : (
        <div className="space-y-8">
          {groups.map((group) => (
            <section key={group.rarity} className="space-y-3">
              <h2 className="border-b border-bk-gold-dim/40 pb-2 text-sm font-bold uppercase tracking-[0.18em] text-bk-gold">
                {group.rarity}
              </h2>
              <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-4">
                {group.items.map((item) => (
                  <StoreItemTile
                    key={String(item.itemId)}
                    item={item}
                    badge={badge(item)}
                    onClick={setSelected}
                  />
                ))}
              </div>
            </section>
          ))}
        </div>
      )}

      <StoreItemModal
        selectedItem={selected}
        onClose={() => setSelected(undefined)}
      />
    </BkPage>
  );
}
